import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase } from '../Interface';
import dynamicFormService from '../../service/dynamicForm.service';
import DynamicFormCreatorFields from './DynamicFormCreatorFields';
import DynamicFormCreatorReports from './DynamicFormCreatorReports';
import DynamicFormCreatorTabs from './DynamicFormCreatorTabs';
import { DynmicFormFieldType } from '../../config/DynmicFormFieldType';
import { normalizeDynamicFormFieldOrder } from '../../utils/dynamicFormFieldOrder';

export const DynamicFormCreatorPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const defaultDynamicFormField = {
    id: null,
    name: '',
    type: DynmicFormFieldType.TEXT,
    visOrder: 0,
    defaultValue: null,
    select: '',
    autocomplete: false,
    multiselect: false,
    userIdMatch: false,
    required: false,
    onlyWithoutProject: false,
    systemRole: null,
    dynamicFormFieldOption: [],
    staticText: '',
  };

  const def = {
    name: '',
    active: true,
    worksheet: false,
    projectRequired: true,
    worksheetEmailTemplateId: null,
    dynamicFormField: [],
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('dynamicFormCreator');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    active: Yup.boolean(),
    worksheet: Yup.boolean(),
    projectRequired: Yup.boolean(),
    worksheetEmailTemplateId: Yup.number().integer().positive().nullable(),
    dynamicFormField: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(),
        type: Yup.string().required(),
        visOrder: Yup.number().required(),
        defaultValue: Yup.number().nullable(),
        select: Yup.string().nullable(),
        autocomplete: Yup.boolean(),
        multiselect: Yup.boolean(),
        userIdMatch: Yup.boolean(),
        required: Yup.boolean(),
        onlyWithoutProject: Yup.boolean(),
        systemRole: Yup.string().nullable(),
        dynamicFormFieldOption: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().required(),
          })
        ),
        /*staticText: Yup.string().when('type', {
           is: (activity, operationDownTime) => !activity && !operationDownTime,,
          then: Yup.string().required('A statikus szöveg megadása kötelező!'),
          otherwise: Yup.string().nullable(),
        }),*/
        staticText: Yup.string()
          .when(['type'], {
            is: (type) => type === DynmicFormFieldType.STATIC,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema,
          })
          .nullable(),
      })
    ),
  });

  const valuesToForm = () => {
    const { id, name, active, worksheet, projectRequired, worksheetEmailTemplateId, dynamicFormField, extension, filename, path } = selectedValues;
    setInitValues({
      id,
      name,
      active,
      worksheet: worksheet ?? false,
      projectRequired: projectRequired ?? true,
      worksheetEmailTemplateId: worksheetEmailTemplateId ?? null,
      dynamicFormField: normalizeDynamicFormFieldOrder(dynamicFormField),
      extension,
      filename,
      path,
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      dynamicFormField: normalizeDynamicFormFieldOrder(values.dynamicFormField),
    };

    dynamicFormService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...values,
      dynamicFormField: normalizeDynamicFormFieldOrder(values.dynamicFormField),
    };

    dynamicFormService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };
  return (
    <FormBase
      name="DynamicFormCreator"
      leftPanelContent={
        <>
          <div className="h-full overflow-auto custom-scrollbar">
            <FormHeaderUI
              addClick={() => {
                setSelectedValues(defaultSelectedValues);
                setViewField(false);
                setAddField(false);
              }}
              addDisabled={!viewField || !canEdit}
              modClick={() => {
                setViewField(false);
                setEditButtonvisible(false);
              }}
              modDisabled={!viewField || operatorsDisabled || !canEdit}
              copyClick={() => {
                setViewField(false);
                setAddField(false);
              }}
              copyDisabled={!viewField || operatorsDisabled || !canEdit}
            />

            <Formik
              initialValues={initValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ resetForm }) => (
                <Form>
                  <DynamicFormCreatorFields viewField={viewField} selectedValues={selectedValues} addField={addField} />

                  <DynamicFormCreatorTabs
                    disabled={viewField}
                    refetchState={{ refetchTable, setRefetchTable }}
                    selectedValuesState={{ selectedValues, setSelectedValues }}
                    defaultDynamicFormField={defaultDynamicFormField}
                  />

                  <FormButtonPanelUI
                    onCancelClick={() => {
                      setViewField(true);
                      setEditButtonvisible(true);
                      setAddField(true);

                      resetForm();
                    }}
                    disabled={editButtonVisible && addField}
                  />
                </Form>
              )}
            </Formik>
          </div>
        </>
      }
      rightPanelContent={
        <DynamicFormCreatorReports
          refetch={{ refetchTable, setRefetchTable }}
          values={{ selectedValues, setSelectedValues }}
          viewField={viewField}
          operatorsDisabled={operatorsDisabled}
          rowSelection={{ rowSelectionModel, setRowSelectionModel }}
        />
      }
    />
  );
};

export default DynamicFormCreatorPage;
